import React, { FC, useEffect, useMemo, useCallback } from 'react';
import { FlatList, StyleSheet, Text, View } from 'react-native';
import Animated, { useSharedValue, useAnimatedStyle, withTiming, runOnJS } from 'react-native-reanimated';
import { EventsPerspective, NCFileBatchEvent, UseTableColumnsParams } from '@v2/pages/common/components/NCFileBatchEvents/types';
import { font as initialFont } from '@styles/fonts';
import { scale as initialScale } from '@components/frame';
import EventBarChart from './EventBarChart';
import EventStatusSubStatusExecMode, { EventStatusSubStatusExecModeProps } from './EventStatusSubStatusExecMode';
import { DURATION_COLORS } from '@v2/pages/common/components/NCFileBatchEvents/constants';
import EventTimeDisplay from './EventTimeDisplay';
import { SmallLoading } from '@components/loading';
import i18n from 'i18n-js';
import TimeDisplayInStaticFormat from '@components/common/TimeDisplayInStaticFormat';
import { getRowKey, getStartBlockText } from '@v2/pages/common/components/NCFileBatchEvents/helpers';
import { NcFileBatchEventType } from '@v2/pages/common/enums';
import { machineStatusColorName, Popup } from '@helpers';
import AccordionTriggerIconCell from '@components/settings/components/common/animatedTable/components/AccordionTriggerIconCell';
import TraceEventPopup from './TraceEventPopup';
import { MachineEventSearchItem } from '@interfaces/machines';
import TouchableElement from '@components/common/touchableElement';
import { useAccordionRow } from '@components/settings/components/common/animatedTable/hooks/useAccordionRow';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { closeComplete } from '@reducers/v2/tableAccordion/tableAccordion.slice';
import { ContainerStyle } from '@interfaces/base';
import ReadyPcsQuantity from '@components/common/ReadyPcsV2/components/ReadyPcsQuantity';

export interface AnimatedEventRowProps
  extends Pick<
      UseTableColumnsParams,
      | 'barChartData'
      | 'textConfig'
      | 'eventType'
      | 'shouldDisplayBatch'
      | 'shouldDisplayMachine'
      | 'shouldShowBarChart'
      | 'shouldDisplayQuantity'
      | 'shouldDisplayQuantityDelta'
      | 'shouldDisplayVersion'
      | 'shouldDisplayStatusSubStatusExecutionMode'
      | 'shouldDisplayAccordionTrigger'
      | 'shouldDisplayNCFileName'
      | 'C'
      | 'toggleAccordion'
      | 'expandedRows'
      | 'eventsPerspective'
      | 'font'
      | 'scale'
    >,
    Pick<EventStatusSubStatusExecModeProps, 'event'> {
  index: number;
  processedEvents: NCFileBatchEvent[];
}

const AnimatedEventRow: FC<AnimatedEventRowProps> = ({
  event,
  eventsPerspective,
  index,
  processedEvents,
  eventType,
  textConfig,
  barChartData,
  shouldDisplayBatch,
  shouldDisplayMachine,
  shouldShowBarChart,
  shouldDisplayQuantity,
  shouldDisplayQuantityDelta,
  shouldDisplayVersion,
  shouldDisplayStatusSubStatusExecutionMode,
  shouldDisplayAccordionTrigger,
  shouldDisplayNCFileName,
  toggleAccordion,
  C,
  font = initialFont,
  scale = initialScale,
}) => {
  const dispatch = useAppDispatch();
  const lineSize = scale(84);
  const subRowLineSize = scale(126);

  const isMachinesPerspective =
    !!eventsPerspective && [EventsPerspective.SINGLE_MACHINE, EventsPerspective.MULTI_MACHINES].includes(eventsPerspective);

  // Get accordion state from Redux
  // Always call hook unconditionally (Rules of Hooks)
  const rowKey = getRowKey(eventType, event);
  const accordionState = useAccordionRow(rowKey);
  const isAccordionEnabled = shouldDisplayAccordionTrigger;

  // Calculate expected height based on accordion state
  // Note: We calculate height even when isClosing to properly animate from current height
  const expectedHeight = useMemo(() => {
    if (!isAccordionEnabled || !accordionState?.isExpanded) return 0;

    // Loading state: always 1 line height
    if (accordionState.isLoading) {
      return lineSize;
    }

    // Data loaded: calculate based on number of items
    if (accordionState.data) {
      const accordionData = accordionState.data as { traceEvents?: any[]; rawMachineEvents?: Record<number, any> | null };
      const traceEvents = accordionData.traceEvents || [];

      // No data: 1 line for NoData component
      if (traceEvents.length === 0) {
        return lineSize;
      }

      // Data exists: number of items * subrow height, with safety max of 1000
      const calculatedHeight = traceEvents.length * subRowLineSize;
      return Math.min(calculatedHeight, 1000);
    }

    // Fallback: 1 line height
    return lineSize;
  }, [isAccordionEnabled, accordionState?.isExpanded, accordionState?.isLoading, accordionState?.data, lineSize, subRowLineSize]);

  // Track expanded height for closing animation
  const expandedHeight = useSharedValue(0);

  // Accordion animations using Reanimated
  const height = useSharedValue(0);
  const opacity = useSharedValue(0);

  // Worklet-safe animation complete callback
  const handleAnimationComplete = useCallback(() => {
    dispatch(closeComplete({ rowKey }));
  }, [dispatch, rowKey]);

  useEffect(() => {
    if (accordionState?.isExpanded && !accordionState.isClosing) {
      // Opening animation - animate immediately to expectedHeight
      if (expectedHeight > 0) {
        expandedHeight.value = expectedHeight;
        height.value = withTiming(expectedHeight, { duration: 250 });
        opacity.value = withTiming(1, { duration: 200 });
      }
    } else if (accordionState?.isClosing) {
      // Closing animation - animate from current expanded height to 0
      height.value = withTiming(0, { duration: 250 }, (finished) => {
        if (finished) {
          runOnJS(handleAnimationComplete)();
        }
      });
      opacity.value = withTiming(0, { duration: 200 });
    }
  }, [accordionState?.isExpanded, accordionState?.isClosing, expectedHeight, handleAnimationComplete]);

  const animatedStyle = useAnimatedStyle(() => ({
    height: height.value,
    opacity: opacity.value,
    overflow: 'hidden',
  }));

  // Determine background color based on accordion state (use Redux only)
  // When closing, immediately return to normal background (don't wait for animation)
  const backgroundColor =
    isAccordionEnabled && accordionState?.isExpanded && !accordionState?.isClosing ? 'Background/Light/Event Grouped' : 'Background/Light/Event';

  const startField = textConfig.start?.[eventType](event);
  const batchText = textConfig.batch?.[eventType]?.(event) ?? '';
  const machineText = textConfig.machine?.[eventType]?.(event) ?? '';

  const shouldDisplayEntityName = shouldDisplayBatch || shouldDisplayMachine || shouldDisplayNCFileName;
  const shouldDisplayAdditionalInfo = isMachinesPerspective;

  // Pre-computed values for conditional rendering
  const ncFileVersionData = event?.ncFileVersionSequenceNumbers || event?.ncFileVersionSequenceNumber;
  const ncFileVersion = ncFileVersionData != null ? (Array.isArray(ncFileVersionData) ? ncFileVersionData[0] : ncFileVersionData) : null;

  const quantityDelta = event?.quantityDelta;
  const shouldDisplayPlusForQuantityDelta = quantityDelta != null && quantityDelta > 0;
  const noDataInLeftColumn = !shouldDisplayVersion && !shouldDisplayQuantityDelta;

  const isTraceEvent = eventType === NcFileBatchEventType.TRACE;

  const onEventRowPress = () => {
    // For non-trace events with accordion enabled, toggle the accordion
    if (!isTraceEvent && isAccordionEnabled && toggleAccordion) {
      toggleAccordion({
        rowKey,
        sequenceNumbers: event.machineEventSequenceNumbers,
        productionBatchId: event.productionBatchId,
        machineId: event.machineId,
      });
      return;
    }

    // For trace events, show the popup
    if (isTraceEvent) {
      Popup({
        Component: (p: any) => <TraceEventPopup onClose={p.hide} event={event as MachineEventSearchItem} />,
        bg: 'Background/Light/Modal Highlight',
      });
    }
  };

  return (
    <>
      {/* Main Row */}
      <TouchableElement onPress={onEventRowPress}>
        {({ pressHoverColor }) => (
          <View
            style={{
              backgroundColor: C(pressHoverColor(backgroundColor)),
              paddingHorizontal: scale(15),
              paddingVertical: scale(10),
              gap: scale(3),
              minHeight: lineSize,
            }}
          >
            <>
              {(shouldDisplayEntityName || shouldDisplayAccordionTrigger) && (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: !shouldDisplayEntityName && shouldDisplayAccordionTrigger ? 'flex-end' : 'space-between',
                  }}
                >
                  {shouldDisplayEntityName && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(5) }}>
                      {shouldDisplayBatch && <Text style={[font['Subhead 2'], { color: C('Label/Light/Primary') }]}>{batchText}</Text>}
                      {shouldDisplayMachine && <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{machineText}</Text>}
                      {shouldDisplayNCFileName && (
                        <Text numberOfLines={1} ellipsizeMode="tail" style={[font['Subhead 2'], { color: C('Label/Light/Primary') }]}>
                          {event?.ncFileName}
                        </Text>
                      )}
                    </View>
                  )}
                  {shouldDisplayAccordionTrigger && (
                    <AccordionTriggerIconCell isExpanded={!!accordionState?.isExpanded} scale={scale} sequenceNumber={event.sequenceNumber} />
                  )}
                </View>
              )}
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(16) }}>
                <View style={{ flex: 1 }}>
                  {shouldShowBarChart && barChartData && barChartData[index] && (
                    <EventBarChart
                      eventType={eventType}
                      eventsPerspective={eventsPerspective}
                      activeTimeInSec={barChartData[index].activeTimeInSec}
                      changeTimeInSec={barChartData[index].changeTimeInSec}
                      setupTimeInSec={barChartData[index].setupTimeInSec}
                      maxTimeInSec={barChartData[index].maxTimeInSec}
                      idealTimeInSec={barChartData[index].idealTimeInSec}
                      paidTimeInSec={barChartData[index].paidTimeInSec}
                      actualTimeInSec={barChartData[index].actualTimeInSec}
                      scheduledTimeInSec={barChartData[index].scheduledTimeInSec}
                      showDots={barChartData[index].showDots}
                      C={C}
                      scale={scale}
                    />
                  )}
                  {shouldDisplayStatusSubStatusExecutionMode && (
                    <EventStatusSubStatusExecMode {...{ C, event, font, scale, includeExtraInfoBasedOnNCFileStatus: shouldDisplayAdditionalInfo }} />
                  )}
                </View>
                <View style={{ flexShrink: 0 }}>
                  <TimeDisplayInStaticFormat
                    timeValue={textConfig.duration?.[eventType]?.(event) ?? 0}
                    C={C}
                    color={eventType === NcFileBatchEventType.TRACE ? machineStatusColorName[event?.status] : DURATION_COLORS[eventType]}
                    fontForDigit={'Time 3'}
                    fontForSecondDigit={'Time 4'}
                    fontForDelimiter={'Caption 1'}
                  />
                </View>
              </View>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <View style={{ gap: scale(3) }}>
                  {(shouldDisplayVersion || shouldDisplayQuantityDelta) && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(8) }}>
                      {shouldDisplayVersion && ncFileVersion != null && (
                        <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>v{ncFileVersion}</Text>
                      )}
                      {!shouldDisplayQuantity && shouldDisplayQuantityDelta && !!quantityDelta && (
                        <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>
                          {shouldDisplayPlusForQuantityDelta && '+'}
                          {quantityDelta}
                        </Text>
                      )}
                      {shouldDisplayQuantity && (
                        <ReadyPcsQuantity
                          C={C}
                          colorForQuantityProduced={'Label/Light/Primary'}
                          colorForQuantityTarget={'Label/Light/Tertiary'}
                          fontForQuantityProduced={'Time 3'}
                          fontForQuantityTarget={'Time 4'}
                          quantityProduced={event?.productionBatchQuantityCumulative ?? ''}
                          quantityTarget={event?.productionBatchQuantityTotal ?? ''}
                        />
                      )}
                    </View>
                  )}
                </View>
                <View style={[{ position: 'absolute', bottom: noDataInLeftColumn ? -scale(18) : 0, right: scale(0) }]}>
                  {startField && <EventTimeDisplay timeValue={startField ?? 0} C={C} font={font} />}
                </View>
              </View>
            </>
          </View>
        )}
      </TouchableElement>

      {/* Accordion Content - Subrows rendered inline from Redux */}
      {isAccordionEnabled && accordionState?.isExpanded && (
        <Animated.View style={[animatedStyle, { pointerEvents: accordionState.isClosing ? 'none' : 'auto' }]}>
          {accordionState.isLoading ? (
            <View
              style={{
                backgroundColor: C('Background/Light/Event Trace'),
                paddingHorizontal: scale(15),
                paddingVertical: scale(10),
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: lineSize,
              }}
            >
              <SmallLoading />
            </View>
          ) : accordionState.data ? (
            (() => {
              const accordionData = accordionState.data as { traceEvents?: any[]; rawMachineEvents?: Record<number, any> | null };
              const traceEvents = accordionData.traceEvents || [];

              if (traceEvents.length === 0) {
                return (
                  <View
                    style={{
                      backgroundColor: C('Background/Light/Event Trace'),
                      paddingHorizontal: scale(15),
                      paddingVertical: scale(10),
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: lineSize,
                    }}
                  >
                    <Text style={[font.Body, { color: C('Label/Light/Primary') }]}>{i18n.t('No data')}</Text>
                  </View>
                );
              }

              return traceEvents.map((traceEvent: any, subIndex: number) => {
                const sequenceNumber = traceEvent.sequenceNumber;
                const rawMachineEventData = sequenceNumber != null ? accordionData.rawMachineEvents?.[sequenceNumber] : null;

                // Subrow data
                const subStartField = textConfig.start?.[NcFileBatchEventType.TRACE](traceEvent);
                const countingMethod = rawMachineEventData?.machineCountingDetails?.countingMethod;
                const startBlock = rawMachineEventData?.currentBlockNumberRaw;
                const subNcFileVersionData = traceEvent?.ncFileVersionSequenceNumbers || traceEvent?.ncFileVersionSequenceNumber;
                const subNcFileVersion =
                  subNcFileVersionData != null ? (Array.isArray(subNcFileVersionData) ? subNcFileVersionData[0] : subNcFileVersionData) : null;
                const subQuantityDelta = traceEvent?.quantityDelta;
                const subShouldDisplayPlus = subQuantityDelta != null && subQuantityDelta > 0;

                const subQuantityDeltaExist = !!subQuantityDelta;

                const onSubRowPress = () => {
                  Popup({
                    Component: (p: any) => <TraceEventPopup onClose={p.hide} event={traceEvent as MachineEventSearchItem} />,
                    bg: 'Background/Light/Modal Highlight',
                  });
                };

                return (
                  <TouchableElement key={`subrow-${sequenceNumber}-${subIndex}`} onPress={onSubRowPress}>
                    {({ pressHoverColor }) => (
                      <View
                        style={{
                          backgroundColor: C(pressHoverColor('Background/Light/Event Trace')),
                          paddingHorizontal: scale(15),
                          paddingVertical: scale(10),
                          gap: scale(3),
                          minHeight: subRowLineSize,
                        }}
                      >
                        {/* Subrow content */}
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(16) }}>
                          <View style={{ flex: 1 }}>
                            <EventStatusSubStatusExecMode {...{ C, event: traceEvent, font, scale }} />
                          </View>
                          <View style={{ flexShrink: 0 }}>
                            <TimeDisplayInStaticFormat
                              timeValue={textConfig.duration?.[NcFileBatchEventType.TRACE]?.(traceEvent) ?? 0}
                              C={C}
                              color={machineStatusColorName[traceEvent?.status]}
                              fontForDigit={'Time 3'}
                              fontForSecondDigit={'Time 4'}
                              fontForDelimiter={'Caption 1'}
                            />
                          </View>
                        </View>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                          <View style={{ gap: scale(3) }}>
                            {countingMethod && subQuantityDeltaExist && (
                              <Text numberOfLines={1} ellipsizeMode="tail" style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>
                                {i18n.t('Counting Method')}: {countingMethod}
                              </Text>
                            )}
                            {startBlock && (
                              <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>
                                {i18n.t('Start Block')}: {getStartBlockText(startBlock)}
                              </Text>
                            )}
                            {(shouldDisplayVersion || shouldDisplayQuantityDelta) && (
                              <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(8) }}>
                                {shouldDisplayVersion && subNcFileVersion != null && (
                                  <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>v{subNcFileVersion}</Text>
                                )}
                                {shouldDisplayQuantityDelta && !!subQuantityDelta && (
                                  <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>
                                    {subShouldDisplayPlus && '+'}
                                    {subQuantityDelta}
                                  </Text>
                                )}
                              </View>
                            )}
                          </View>
                          <View style={{ position: 'absolute', bottom: 0, right: 0 }}>
                            {subStartField && <EventTimeDisplay timeValue={subStartField ?? 0} C={C} font={font} />}
                          </View>
                        </View>
                      </View>
                    )}
                  </TouchableElement>
                );
              });
            })()
          ) : null}
        </Animated.View>
      )}

      {/* Separator */}
      {index !== processedEvents.length - 1 && (
        <View style={{ height: StyleSheet.hairlineWidth, flex: 1, backgroundColor: C('Hairline/Light/No trans'), marginLeft: scale(15) }} />
      )}
    </>
  );
};

interface NCFileBatchEventsMobileProps
  extends ContainerStyle,
    Pick<
      UseTableColumnsParams,
      | 'C'
      | 'scale'
      | 'font'
      | 'barChartData'
      | 'textConfig'
      | 'eventType'
      | 'shouldDisplayBatch'
      | 'shouldDisplayMachine'
      | 'shouldShowBarChart'
      | 'shouldDisplayStatusSubStatusExecutionMode'
      | 'shouldDisplayQuantity'
      | 'shouldDisplayQuantityDelta'
      | 'shouldDisplayVersion'
      | 'shouldDisplayNCFileName'
      | 'expandedRows' // Now Redux Record instead of Set
      | 'toggleAccordion'
      | 'eventsPerspective'
    > {
  events: NCFileBatchEvent[];
  isLoadingMore: boolean;
  isLoading: boolean;
  shouldDisplayAccordionTrigger?: boolean;
  onEndReached?: () => void;
  onEndReachedThreshold?: number;
  keyExtractorFields?: readonly string[];
}

const NCFileBatchEventsMobile: FC<NCFileBatchEventsMobileProps> = ({
  events,
  eventsPerspective,
  barChartData,
  C,
  textConfig,
  eventType,
  shouldDisplayBatch,
  shouldDisplayMachine,
  shouldShowBarChart,
  shouldDisplayQuantity,
  shouldDisplayQuantityDelta,
  shouldDisplayVersion,
  shouldDisplayStatusSubStatusExecutionMode,
  shouldDisplayNCFileName,
  isLoadingMore,
  isLoading,
  toggleAccordion,
  containerStyle,
  onEndReached,
  onEndReachedThreshold = 0.5,
  font = initialFont,
  scale = initialScale,
  expandedRows = {}, // Now Redux Record, defaults to empty object
  shouldDisplayAccordionTrigger = false,
  keyExtractorFields,
}) => {
  const getItemKey = (item: NCFileBatchEvent, index: number): string => {
    if (keyExtractorFields) {
      for (const field of keyExtractorFields) {
        if (field in item && item[field as keyof NCFileBatchEvent] != null) {
          return String(item[field as keyof NCFileBatchEvent]);
        }
      }
    }
    return String(index);
  };
  if (isLoading) {
    return (
      <View style={[{ flex: 1, alignItems: 'center', marginTop: scale(20) }]}>
        <SmallLoading />
      </View>
    );
  }

  if (events.length === 0) {
    return (
      <View style={[{ flex: 1, alignItems: 'center', marginTop: scale(20) }]}>
        <Text style={[font.Body, { color: C('Label/Light/Primary') }]}>{i18n.t('No data')}</Text>
      </View>
    );
  }

  // Use events array directly - no manipulation needed

  const renderItem = ({ item: event, index }: { item: NCFileBatchEvent; index: number }) => (
    <AnimatedEventRow
      key={`event-${index}`}
      event={event}
      eventsPerspective={eventsPerspective}
      index={index}
      processedEvents={events}
      eventType={eventType}
      textConfig={textConfig}
      barChartData={barChartData}
      shouldDisplayBatch={shouldDisplayBatch}
      shouldDisplayMachine={shouldDisplayMachine}
      shouldShowBarChart={shouldShowBarChart}
      shouldDisplayQuantity={shouldDisplayQuantity}
      shouldDisplayQuantityDelta={shouldDisplayQuantityDelta}
      shouldDisplayVersion={shouldDisplayVersion}
      shouldDisplayNCFileName={shouldDisplayNCFileName}
      shouldDisplayStatusSubStatusExecutionMode={shouldDisplayStatusSubStatusExecutionMode}
      shouldDisplayAccordionTrigger={shouldDisplayAccordionTrigger}
      expandedRows={expandedRows}
      toggleAccordion={toggleAccordion}
      C={C}
      font={font}
      scale={scale}
    />
  );

  const ListFooterComponent = () =>
    isLoadingMore ? (
      <View style={{ flex: 1, width: '100%', flexDirection: 'row', justifyContent: 'center', paddingVertical: scale(10) }}>
        <SmallLoading />
      </View>
    ) : null;

  return (
    <View style={[{ flex: 1 }, containerStyle]}>
      <FlatList
        data={events}
        renderItem={renderItem}
        keyExtractor={(event, index) => `event-${getItemKey(event, index)}`}
        showsVerticalScrollIndicator={false}
        onEndReached={onEndReached}
        onEndReachedThreshold={onEndReachedThreshold}
        ListFooterComponent={ListFooterComponent}
        extraData={expandedRows}
        removeClippedSubviews={false}
      />
    </View>
  );
};

export { NCFileBatchEventsMobile };
